/**
 * Responsibility: decide which holdings a set of filters lets through.
 *
 * Separate from the controls that set them and from the views that show the
 * result, because it is the only part of the three that can be wrong in a
 * way nobody sees. A filter that silently drops a card looks exactly like a
 * collection that does not contain it.
 *
 * Every field is "no opinion" when empty, and an empty filter set returns
 * the collection unchanged. That matters more than it sounds: the controls
 * persist across sessions, so a filter left on last week would otherwise
 * make the page look empty on a later visit with no clue why. The page
 * shows the count it hid for the same reason.
 *
 * Set and language are read off the product key by the hub - see
 * `product_facets` - rather than being stored on a holding. Grade is parsed
 * out of the label here, because "PSA 10", "Grade 9.5" and "10" are all
 * things that end up in that field.
 */
import type { CollectionItem } from '../../api/client';

export type CollectionFilters = {
  /** '' any, 'RAW' ungraded only, 'GRADED' slabs only. */
  slab: '' | 'RAW' | 'GRADED';
  /** Grading company, exact. Empty means any. */
  company: string;
  /** Inclusive grade bounds, as typed. Empty means unbounded. */
  minGrade: string;
  maxGrade: string;
  /** Derived set label, exact. Empty means any. */
  set: string;
  /** 'SPORTS' | 'TCG'. Empty means any. */
  category: string;
  /** Print language code: 'en', 'jp', ... Empty means any. */
  language: string;
  /** Inclusive bounds on the position's value. Empty means unbounded. */
  minValue: string;
  maxValue: string;
  /** ISO dates, inclusive, against when the card was catalogued. */
  addedFrom: string;
  addedTo: string;
  /** Sub-collection id, exact. Empty means any.
   *
   * An id rather than a name, matching what a holding stores, so a tag
   * renamed while a filter is saved keeps filtering the same cards. */
  tag: string;
};

export const NO_FILTERS: CollectionFilters = {
  slab: '',
  company: '',
  minGrade: '',
  maxGrade: '',
  set: '',
  category: '',
  language: '',
  minValue: '',
  maxValue: '',
  addedFrom: '',
  addedTo: '',
  tag: '',
};

/** Language codes the hub can derive, with names for the menu. */
export const LANGUAGES: Record<string, string> = {
  en: 'English',
  jp: 'Japanese',
  cn: 'Chinese',
  ko: 'Korean',
  de: 'German',
  fr: 'French',
  it: 'Italian',
  es: 'Spanish',
  pt: 'Portuguese',
  id: 'Indonesian',
  th: 'Thai',
  ru: 'Russian',
};

/**
 * The number in a grade label, or null when there is not one.
 *
 * Null is not zero. A raw card has no grade, and treating it as a nought
 * would sweep every ungraded copy into a "grade 8 and under" filter.
 */
export function gradeNumber(label: string | null): number | null {
  if (!label) return null;
  const found = /(\d+(?:\.\d)?)/.exec(label);
  return found ? Number(found[1]) : null;
}

function within(value: number | null, min: string, max: string): boolean {
  if (min === '' && max === '') return true;
  // Something unmeasurable cannot satisfy a bound, and saying it does would
  // put unvalued holdings inside every value range.
  if (value === null) return false;
  if (min !== '' && value < Number(min)) return false;
  return !(max !== '' && value > Number(max));
}

function matches(item: CollectionItem, filters: CollectionFilters): boolean {
  if (filters.slab === 'RAW' && item.is_graded) return false;
  if (filters.slab === 'GRADED' && !item.is_graded) return false;
  if (filters.company && item.grading_company !== filters.company) return false;
  if (filters.category && item.category !== filters.category) return false;
  if (filters.set && item.set_label !== filters.set) return false;
  if (filters.language && item.language !== filters.language) return false;
  // Optional on the generated schema because the server defaults it.
  if (filters.tag && !(item.tags ?? []).includes(filters.tag)) return false;

  // A grade belongs to a slab. A raw copy can still carry a number in its
  // label - somebody typed "PSA 10" without naming a grader - and letting
  // that satisfy a grade range would put ungraded cards inside one.
  const grade = item.is_graded ? gradeNumber(item.grade_label) : null;
  if (!within(grade, filters.minGrade, filters.maxGrade)) return false;

  const value = item.holding_value === null ? null : Number(item.holding_value);
  if (!within(value, filters.minValue, filters.maxValue)) return false;

  // Compared as ISO date strings: `added_at` is a timestamp, so it is cut
  // to its date before being held against a date bound. Otherwise
  // "added up to today" excludes everything added today.
  const added = item.added_at.slice(0, 10);
  if (filters.addedFrom && added < filters.addedFrom) return false;
  return !(filters.addedTo && added > filters.addedTo);
}

export function applyFilters(
  items: CollectionItem[],
  filters: CollectionFilters,
): CollectionItem[] {
  return items.filter((item) => matches(item, filters));
}

/** True when nothing is set, so the page can skip the "n hidden" line. */
export function isUnfiltered(filters: CollectionFilters): boolean {
  return (Object.keys(NO_FILTERS) as (keyof CollectionFilters)[]).every(
    (key) => filters[key] === NO_FILTERS[key],
  );
}

/**
 * How many filters are set.
 *
 * Shown on the button, because the controls are behind it now and a filter
 * nobody can see is a filter nobody can explain.
 */
export function activeCount(filters: CollectionFilters): number {
  return (Object.keys(NO_FILTERS) as (keyof CollectionFilters)[]).filter(
    (key) => filters[key] !== NO_FILTERS[key],
  ).length;
}

/**
 * The options a menu should offer, taken from the collection itself.
 *
 * Offering every grading company in existence when three cards are owned
 * makes the operator hunt for the one that applies. A value that is not in
 * the collection cannot narrow it, so it is not worth listing.
 */
export function optionsFrom(items: CollectionItem[]) {
  const companies = new Set<string>();
  const sets = new Set<string>();
  const languages = new Set<string>();
  for (const item of items) {
    if (item.grading_company) companies.add(item.grading_company);
    if (item.set_label) sets.add(item.set_label);
    languages.add(item.language);
  }
  return {
    companies: [...companies].sort(),
    sets: [...sets].sort(),
    languages: [...languages].sort(),
  };
}
